/**
 * PricingSettingsPanel — admin UI for W15 deterministic pricing System Settings.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Form,
  Input,
  InputNumber,
  Switch,
  Typography,
  Tag,
  Space,
} from "antd";
import styled from "styled-components";
import ExpandableSections from "@/shared/components/ExpandableSections.jsx";
import {
  PRICING_FIELD_META,
  PRICING_SECTIONS,
  PRICING_SETTING_KEYS,
  DEMOTED_PRICING_SETTING_KEYS,
  RETIRED_PRICING_SETTING_KEYS,
  getPricingFieldMeta,
} from "../utils/pricingSettingsMeta.js";
import {
  getPricingFieldHelp,
  getPricingSectionHelp,
} from "../utils/pricingSettingsHelp.js";
import { assessCorePricingReadiness } from "../utils/pricingSettingsReadiness.js";
import PricingFieldHelpBubble from "./PricingFieldHelpBubble.jsx";
import SeasonOpsBrief from "@/features/pricing/components/SeasonOpsBrief.jsx";
import { fetchSeasonBriefing } from "@/features/pricing/services/rateCardsService.js";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const FieldBlock = styled.div`
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const SectionHint = styled(Paragraph)`
  && {
    margin-top: 0;
    margin-bottom: 12px;
  }
`;

const JSON_ARRAY_KEYS = new Set([
  "pricing.vehicles.bands",
  "pricing.corridors.catalogue",
  "pricing.corridors.locationRegionMap",
  "pricing.exclusive.catalogue",
  "pricing.season.calendar",
]);

/**
 * @param {object} props
 * @param {object[]} [props.settings] - Loaded settings from API (pricing category)
 */
const PricingSettingsPanel = ({ settings = [] }) => {
  const form = Form.useFormInstance();
  const watchedValues = Form.useWatch([], form) || {};

  const settingsByKey = useMemo(() => {
    const map = {};
    settings.forEach((setting) => {
      map[setting.key] = setting;
    });
    return map;
  }, [settings]);

  const readiness = useMemo(
    () => assessCorePricingReadiness(watchedValues),
    [watchedValues]
  );

  const automationEnabled = watchedValues["pricing.automation.enabled"] === true;
  const seasonEnabled = watchedValues["pricing.season.enabled"] === true;
  const [seasonBriefing, setSeasonBriefing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchSeasonBriefing()
      .then((data) => {
        if (!cancelled) setSeasonBriefing(data || null);
      })
      .catch(() => {
        if (!cancelled) setSeasonBriefing(null);
      });
    return () => {
      cancelled = true;
    };
  }, [seasonEnabled, settings]);

  const fieldsBySection = useMemo(() => {
    /** @type {Record<string, string[]>} */
    const grouped = {};
    for (const section of PRICING_SECTIONS) {
      grouped[section.key] = [];
    }

    const keys = new Set([
      ...PRICING_SETTING_KEYS,
      ...settings.map((s) => s.key),
    ]);

    keys.forEach((key) => {
      if (!key.startsWith("pricing.")) {
        return;
      }
      if (RETIRED_PRICING_SETTING_KEYS.includes(key)) {
        return;
      }
      if (DEMOTED_PRICING_SETTING_KEYS.includes(key)) {
        return;
      }
      const meta = getPricingFieldMeta(key);
      const sectionKey = meta.section || "policy";
      if (!grouped[sectionKey]) {
        grouped[sectionKey] = [];
      }
      grouped[sectionKey].push(key);
    });

    return grouped;
  }, [settings]);

  const renderFieldControl = (key, meta) => {
    const { type, metadata = {} } = meta;

    switch (type) {
      case "boolean":
        return <Switch />;
      case "number":
        return (
          <InputNumber
            style={{ width: "100%" }}
            min={metadata.min}
            max={metadata.max}
            step={metadata.step || 1}
            formatter={
              metadata.unit === "percent"
                ? (value) => (value != null ? `${value}%` : "")
                : metadata.unit === "ZAR"
                  ? (value) =>
                      value != null && value !== ""
                        ? `R ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                        : ""
                  : undefined
            }
            parser={
              metadata.unit === "percent"
                ? (value) => value.replace("%", "")
                : metadata.unit === "ZAR"
                  ? (value) => value.replace(/R\s?|(,*)/g, "")
                  : undefined
            }
          />
        );
      case "array":
      case "object":
        return (
          <TextArea
            rows={
              key === "pricing.corridors.catalogue" ||
              key === "pricing.corridors.locationRegionMap"
                ? 10
                : 6
            }
            style={{ fontFamily: "monospace", fontSize: 12 }}
            placeholder={
              key === "pricing.corridors.catalogue"
                ? '[{"id":"GP-WC","originRegion":"GP","destinationRegion":"WC","serviceType":"shared_load","minimumAmount":1500,"ratePerM3":390,"enabled":true}]'
                : key === "pricing.corridors.locationRegionMap"
                  ? '[{"matchType":"city","matchValue":"George","regionCode":"WC","active":true}]'
                  : '[{"id":"band-1","usableM3":10,"label":"1-ton"}]'
            }
          />
        );
      default:
        return <Input />;
    }
  };

  const expandableSections = useMemo(() => {
    return PRICING_SECTIONS.map((section) => {
      const fieldKeys = fieldsBySection[section.key] || [];
      if (fieldKeys.length === 0) {
        return null;
      }

      let extra = null;
      if (section.key === "season") {
        extra = (
          <Tag color={seasonEnabled ? "green" : "default"}>
            {seasonEnabled ? "Live" : "Off"}
          </Tag>
        );
      }

      return {
        key: section.key,
        title: (
          <Space size="small">
            <span>{section.title}</span>
            {getPricingSectionHelp(section.key) && (
              <PricingFieldHelpBubble
                size="section"
                help={{
                  title: section.title,
                  ...getPricingSectionHelp(section.key),
                }}
              />
            )}
          </Space>
        ),
        extra,
        // Keep Form.Items mounted when collapsed so Settings save still sees values.
        forceRender: true,
        children: (
          <>
            {section.key === "season" && seasonBriefing ? (
              <div style={{ marginBottom: 12 }}>
                <SeasonOpsBrief briefing={seasonBriefing} compact />
              </div>
            ) : null}
            {section.hint && (
              <SectionHint type="secondary">{section.hint}</SectionHint>
            )}

            {fieldKeys.map((key) => {
              const meta = PRICING_FIELD_META[key] || getPricingFieldMeta(key);
              const dbSetting = settingsByKey[key];
              const description =
                meta.description || dbSetting?.description || "";
              const fieldHelp = getPricingFieldHelp(key);

              return (
                <FieldBlock key={key}>
                  <Form.Item
                    name={key}
                    label={
                      <Space size="small" align="center">
                        <span>{meta.label}</span>
                        {fieldHelp && <PricingFieldHelpBubble help={fieldHelp} />}
                        {meta.monetary && (
                          <Tag color="gold" style={{ margin: 0 }}>
                            Monetary
                          </Tag>
                        )}
                      </Space>
                    }
                    valuePropName={meta.type === "boolean" ? "checked" : "value"}
                    getValueFromEvent={
                      meta.type === "boolean"
                        ? (checked) => checked
                        : undefined
                    }
                    rules={
                      JSON_ARRAY_KEYS.has(key)
                        ? [
                            {
                              validator: async (_, value) => {
                                if (value == null || value === "") {
                                  return;
                                }
                                if (typeof value === "string") {
                                  JSON.parse(value);
                                }
                              },
                              message: "Invalid JSON array",
                            },
                          ]
                        : undefined
                    }
                    extra={
                      <>
                        {description ? (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {description}
                          </Text>
                        ) : null}
                        {fieldHelp?.example && (
                          <Text
                            type="secondary"
                            style={{ fontSize: 12, display: "block", marginTop: 4 }}
                          >
                            Example: {fieldHelp.example.split("\n")[0]}
                            {fieldHelp.example.includes("\n") ? " …" : ""}
                            {" — click the blue info icon for full detail."}
                          </Text>
                        )}
                      </>
                    }
                  >
                    {renderFieldControl(key, meta)}
                  </Form.Item>
                  {meta.metadata?.unit && meta.type === "number" && (
                    <Tag color="blue">Unit: {meta.metadata.unit}</Tag>
                  )}
                </FieldBlock>
              );
            })}
          </>
        ),
      };
    }).filter(Boolean);
  }, [
    fieldsBySection,
    settingsByKey,
    seasonEnabled,
    seasonBriefing,
  ]);

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Alert
        type={readiness.ready ? "success" : "warning"}
        showIcon
        message={
          readiness.ready
            ? "Core pricing values configured"
            : "Core monetary values still required"
        }
        description={
          readiness.ready ? (
            <Text>
              Local base amount and policy version are set. You may enable
              automated pricing when ready.
            </Text>
          ) : (
            <Text>
              Set a positive local base amount before enabling automation.
              {readiness.missingCore.length > 0 && (
                <> Missing: {readiness.missingCore.join(", ")}</>
              )}
            </Text>
          )
        }
      />

      {automationEnabled && !readiness.ready && (
        <Alert
          type="error"
          showIcon
          message="Automation enabled but settings incomplete"
          description="Auto-pricing will produce manual_review_required until core monetary values are approved."
        />
      )}

      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Rate catalogues (Shared Load, Local / Intra, Access & Handling, Exclusive
        LD) live under{" "}
        <Link to="/pricing/rate-cards">Pricing → Rate Cards</Link>. This tab
        keeps automation, season calendar, resources, inventory factors, review
        policy, and internal economics. Do not paste competitor rate sheets into
        settings. Browse inventory SKUs in{" "}
        <Link to="/settings/inventory-catalogue">Inventory catalogue</Link>.
      </Paragraph>

      <ExpandableSections
        defaultOpenKeys={["automation", "season"]}
        sections={expandableSections}
      />
    </Space>
  );
};

export default PricingSettingsPanel;
